import React, { useEffect, useState, useCallback } from "react";
import Table from "@components/table";
import { Transaction } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import styled from "@emotion/styled";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";

const StatusBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid ${(props) => `${props.color}30`};
  text-transform: capitalize;
`;

const AmountText = styled.span<{ positive: boolean }>`
  font-weight: 800;
  font-size: 14px;
  color: ${(props) => (props.positive ? "#10b981" : "#f43f5e")};
  background: ${(props) => (props.positive ? "#10b98115" : "#f43f5e15")};
  padding: 6px 12px;
  border-radius: 8px;
`;

const getStatusIcon = (status: string) => {
  if (!status) return <AlertCircle size={14} />;
  switch (status.toLowerCase()) {
    case "paid":
    case "success":
    case "approved":
    case "completed":
      return <CheckCircle size={14} />;
    case "unpaid":
    case "pending":
    case "processing":
      return <AlertCircle size={14} />;
    case "failed":
    case "cancelled":
      return <XCircle size={14} />;
    default:
      return <AlertCircle size={14} />;
  }
};

const Transactions = ({ state }: { state: any }) => {
  const { settings } = useGeneralSettings();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Transaction.list({
        params: {
          limit: 10,
          page: page,
          userId: state.id,
        },
      });
      setData(res.items || []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.totalItems || 0);
    } catch (error) {
      console.error("Failed to fetch transactions", error);
    } finally {
      setLoading(false);
    }
  }, [page, state.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: "Transaction ID",
      key: "id",
      width: "10%",
      render: (row: any) => (
        <span style={{ color: "#64748b", fontWeight: 700 }}>
          #{row.id || "---"}
        </span>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: "15%",
      render: (row: any) => {
        const isCredit = row.type === "credit";
        return (
          <AmountText positive={isCredit}>
            {isCredit ? "+" : "-"}{" "}
            {Helper.formatCurrency(Math.abs(row.amount || 0), settings)}
          </AmountText>
        );
      },
    },
    {
      title: "Type",
      key: "type",
      width: "12%",
      render: (row: any) => (
        <StatusBadge color={row.type === "credit" ? "#10b981" : "#f43f5e"}>
          {getStatusIcon(row.type === "credit" ? "success" : "failed")}
          {row.type || "Unknown"}
        </StatusBadge>
      ),
    },
    {
      title: "Purpose",
      key: "purpose",
      width: "20%",
      render: (row: any) => (
        <span style={{ color: "#334155", fontWeight: 500 }}>
          {row.purpose || "General Transaction"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "13%",
      render: (row: any) => {
        const color =
          row.status === "Paid"
            ? "#10b981"
            : row.status === "Unpaid"
              ? "#f59e0b"
              : "#64748b";
        return (
          <StatusBadge color={color}>
            {getStatusIcon(row.status)}
            {row.status || "Pending"}
          </StatusBadge>
        );
      },
    },
    {
      title: "Closing Balance",
      key: "closingBalance",
      width: "15%",
      render: (row: any) => (
        <span style={{ fontWeight: 700, color: "#0f172a" }}>
          {row.closingBalance != null
            ? Helper.formatCurrency(row.closingBalance, settings)
            : "---"}
        </span>
      ),
    },
    {
      title: "Date & Time",
      key: "createdAt",
      width: "15%",
      render: (row: any) => (
        <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
          {row.createdAt
            ? new Date(row.createdAt).toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "---"}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: "0" }}>
      <Table
        columns={columns}
        data={data}
        loading={loading}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
      />
    </div>
  );
};

export default Transactions;
